import axios, { AxiosInstance } from 'axios';
import { auth } from '../lib/firebase';
import { getConfig } from '../config/environment';

const { cloudFunctions, cloudRunServices } = getConfig();

/**
 * Builds a Cloud Run service URL using the project number
 * @param serviceName - The name of the Cloud Run service (e.g., 'text-improver')
 * @returns The full Cloud Run service URL
 */
export const getCloudRunServiceUrl = (serviceName: string): string => {
  return `https://${serviceName}-${cloudRunServices.projectNumber}.us-central1.run.app`;
};

/**
 * Adds Firebase auth token interceptor to an axios instance
 * @param instance - The axios instance to add the interceptor to
 */
export const addAuthInterceptor = (instance: AxiosInstance): void => {
  instance.interceptors.request.use(
    async (config) => {
      try {
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken();
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      } catch (error) {
        console.error('Error getting auth token:', error);
        return config;
      }
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

/**
 * Creates an authenticated axios instance for a Cloud Run service
 * @param serviceName - The name of the Cloud Run service (e.g., 'rollout-connection')
 * @returns An axios instance configured with auth interceptor
 */
export const createCloudRunAxios = (serviceName: string): AxiosInstance => {
  const instance = axios.create({
    baseURL: getCloudRunServiceUrl(serviceName),
    headers: {
      'Content-Type': 'application/json',
    },
  });
  addAuthInterceptor(instance);
  return instance;
};

export const cloudFunctionsAxios = axios.create({
  baseURL: cloudFunctions.baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth interceptor to cloud functions axios
addAuthInterceptor(cloudFunctionsAxios);
